<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import UserService from '@/services/user.service'
import RoleCards from '@/views/apps/roles/RoleCards.vue'
import ShowUserDrawer from '@/views/apps/user/list/ShowUserDrawer.vue'
import { userAuthState } from '@core/auth'
import { VDataTableServer } from 'vuetify/labs/VDataTable'


definePage({
  meta: {
    name: 'users',
    subject: 'global.menu',
    action: 'users',
  },  
})

const auth = userAuthState()
const snackbar = useToast()

// 👉 Store
const searchQuery = ref('')
const selectedRole = ref()
const selectedDepartments = ref()
const selectedUnits = ref()
const selectedStatus = ref()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()


const updateOptions = options => {
  page.value = options.page
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  {
    title: 'User',
    key: 'user',
  },
  {
    title: 'Email',
    key: 'email',
  },
  {
    title: 'Role',
    key: 'role',
  },
  {
    title: 'MFA Status',
    key: 'mfa_status',
    sortable: false,
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]

const {
  data: usersData,
  execute: fetchUsers,
} =  await useApi(createUrl('/masters/users', {
  query: {
    paged: 1,
    q: searchQuery,
    status: selectedStatus,
    departments: selectedDepartments,
    roles: selectedRole,
    units: selectedUnits,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
    with: ['roles'],
    appends: ['is_super_admin', 'has_two_factor_enabled', 'two_factor_primary_method'],
  },
}))

const users = computed(() => usersData.value?.data)
const totalUsers = computed(() => usersData.value?.meta?.total)

// 👉 search filters
const { data: departments } =await useApi(createUrl('/masters/departments'))
const { data: roles } =await useApi(createUrl('/masters/roles'))
const { data: units } = await useApi(createUrl('/masters/units'))

const status = [
  {
    title: 'Active',
    value: 1,
  },
  {
    title: 'Inactive',
    value: 0,
  },
]

const resolveUserRoleVariant = roles => {

  const roleNames = roles.map(role => role.name)

  if (roleNames.includes('admin'))
    return { color: 'primary', icon: 'tabler-user-shield' }
  if (roleNames.includes('super-admin'))
    return { color: 'primary', icon: 'tabler-password-user' }
  if (roleNames.includes('user'))
    return { color: 'secondary', icon: 'tabler-user' }
  if (roleNames.includes('investigator'))
    return { color: 'secondary', icon: 'tabler-user-search' }
  if (roleNames.includes('Admin Team'))
    return { color: 'secondary', icon: 'tabler-users-group' }
  if (roleNames.includes('reviewer'))
    return { color: 'secondary', icon: 'tabler-user-edit' }
  if (roleNames.includes('verifier'))
    return { color: 'secondary', icon: 'tabler-user-check' }
  if (roleNames.includes('analyst') || roleNames.includes('incident-analyst'))
    return { color: 'warning', icon: 'tabler-report-analytics' }
  else
    return { color: 'secondary', icon: 'tabler-user' }
}

const resolveUserStatusVariant = stat => {
  const statLowerCase = stat.toLowerCase()
  if (statLowerCase === 'pending')
    return 'warning'
  if (statLowerCase === 'active')
    return 'success'
  if (statLowerCase === 'inactive')
    return 'secondary'
  
  return 'primary'
}

const isShowUserDrawerVisible = ref(false)
const showUserDrawerData = ref()

const showUser = userData => {
  showUserDrawerData.value = userData
  isShowUserDrawerVisible.value = true
}

const deleteUser = async id => {
  await $api(`/masters/users/${ id }`, { method: 'DELETE' }).then(() => {
    fetchUsers()
  })
}

const userDeleteData = ref()
const isDeleteConfirmDialogVisible = ref(false)

const showDeleteConfirmDialog = data => {
  userDeleteData.value = data
  isDeleteConfirmDialogVisible.value = true
}

const userDelete = async (confirm, data) => {
  if (confirm && data?.id) {
    await $api(`/masters/users/${ data.id }`, { method: 'DELETE' }).then(() => {
      fetchUsers()
      isDeleteConfirmDialogVisible.value = false
    })
  }
}

watch(isDeleteConfirmDialogVisible.value, value => {
  if (value === false) {
    userDeleteData.value = {}
  }
})

const exportUsers = () => {
  useApi(createUrl("/masters/export/users"))
}

// ===== Import User Functionality =====
const refImportUserForm = ref()
const importUserFile = ref()
const importUserErrors = ref()
const isImportUserLoading = ref(false)
const isImportUserModal = ref(false)


const submitImportUser = async () => {
  if (!importUserFile.value) {
    importUserErrors.value = {
      file: ["Please select a file to import."],
    }

    return
  }
  isImportUserLoading.value = true
  importUserErrors.value = null // Clear previous errors

  try {
    await UserService.import(
      {
        file: importUserFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("users imported successfully.")
        isImportUserModal.value = false
        fetchUsers() // Refresh the list
        importUserFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importUserErrors.value = error.data?.errors || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
        snackbar.error(error.data?.message || "File upload failed.")
      },
    )
  } catch (e) {
    const errorData = e.data

    importUserErrors.value = errorData?.errors || {
      message: errorData?.message || "File upload failed. Please try again.",
    }
    snackbar.error(
      errorData?.message || "An unexpected error occurred during import.",
    )
    if (!importUserErrors.value && !errorData?.message)
      importUserErrors.value = { message: "Import submission failed." }
  } finally {
    isImportUserLoading.value = false
  }
}

const downloadSampleUserFile = async () => {
  await downloadFile(
    "masters/users/sample-import-file/export",
    "users-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportUserModal.value,
  value => {
    if (value === false) {
      importUserFile.value = null
      importUserErrors.value = null
    }
  },
)

// ===== End Import User Functionality =====
</script>

<template>
  <section>
    <VCol 
      v-if="$can('read', 'master.role')" 
      cols="12"
    >
      <h4 class="text-h4 mb-6">
        Roles List
      </h4>
      <RoleCards />
    </VCol>
    <VCol cols="12">
      <VCard
        title="Filters"
        class="mb-6"
      >
        <VCardText>
          <VRow>
            <!-- 👉 Select Role -->
            <VCol
              cols="12"
              sm="3"
            >
              <AppCombobox
                v-model="selectedRole"
                :items="roles"
                :item-title="item => item.name"
                :item-value="item => item.id"
                label="Roles"
                placeholder="Select Roles"
                :hide-no-data="false"
                :multiple="true"
                :return-object="false"
                :clearable="true"
              />
            </VCol>
            <VCol
              cols="12"
              sm="3"
            >
              <AppCombobox
                v-model="selectedDepartments"
                :items="departments"
                :item-title="item => item.name"
                :item-value="item => item.id"
                label="Departments"
                placeholder="Select Departments"
                :hide-no-data="false"
                :multiple="true"
                :return-object="false"
                :clearable="true"
              />
            </VCol>
            <VCol
              cols="12"
              sm="3"
            >
              <AppSelect
                v-model="selectedUnits"
                label="Units"
                placeholder="Select Units"
                :items="units"
                :item-title="item => item.name"
                :item-value="item => item.id"
                multiple
                clearable
                clear-icon="tabler-x"
              />
            </VCol>
            <VCol
              cols="12"
              sm="3"
            >
              <AppSelect
                v-model="selectedStatus"
                label="Status"
                placeholder="Select Status"
                :items="status"
                clearable
                clear-icon="tabler-x"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>
    <VCol cols="12">
      <h4 class="text-h4 mb-6">
        Users List
      </h4>
      <VCard>
        <VCardText class="d-flex flex-wrap py-4 gap-4">
          <div class="me-3 d-flex gap-3">
            <AppSelect
              :model-value="itemsPerPage"
              :items="[
                { value: 10, title: '10' },
                { value: 25, title: '25' },
                { value: 50, title: '50' },
                { value: 100, title: '100' },
                { value: -1, title: 'All' },
              ]"
              style="inline-size: 6.25rem;"
              @update:model-value="itemsPerPage = parseInt($event, 10)"
            />
          </div>
          <VSpacer />

          <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
            <!-- 👉 Search  -->
            <div style="inline-size: 10rem;">
              <AppTextField
                v-model="searchQuery"
                placeholder="Search"
                density="compact"
              />
            </div>

            <!-- 👉 Export button -->
            <!--
              <VBtn
              variant="tonal"
              color="secondary"
              prepend-icon="tabler-screen-share"
              @click="exportUsers"
              >
              Export
              </VBtn> 
            -->
            <!-- 👉 Import User Button -->
            <VBtn
              v-if="$can('create', 'master.user')"
              prepend-icon="tabler-download"
              @click="isImportUserModal = !isImportUserModal"
            >
              Import Users
            </VBtn>
            <!-- 👉 Add user button -->
            <RouterLink
              v-if="$can('create', 'master.user')"
              :to="{ name: 'app-user-add'}"
              class="font-weight-medium text-link"
            >
              <VBtn prepend-icon="tabler-plus">
                Add New User
              </VBtn>
            </RouterLink>
          </div>
        </VCardText>

        <VDivider />

        <!-- SECTION datatable -->
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :items="users"
          :items-length="totalUsers"
          :headers="headers"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <!-- User -->
          <template #item.user="{ item }">
            <div
              class="d-flex align-center cursor-pointer"
              :userData="item"
              @click="showUser(item)"
            >
              <VAvatar
                size="34"
                :variant="!item.avatar ? 'tonal' : undefined"
                :color="!item.avatar ? resolveUserRoleVariant(item.roles).color : undefined"
                class="me-3"
              >
                <VImg
                  v-if="item.avatar"
                  :src="item.avatar"
                />
                <span v-else>{{ avatarText(item.name) }}</span>
              </VAvatar>
              <div class="d-flex flex-column">
                <h6 class="text-base">
                  {{ item.name }}
                </h6>
                <span class="text-sm text-medium-emphasis">{{ item.employee_code }}</span>
              </div>
            </div>
          </template>

          <!-- 👉 Role -->
          <template #item.role="{ item }">
            <div class="d-flex align-center gap-4">
              <VAvatar
                :size="30"
                :color="resolveUserRoleVariant(item.roles).color"
                variant="tonal"
              >
                <VIcon
                  :size="20"
                  :icon="resolveUserRoleVariant(item.roles).icon"
                />
              </VAvatar>
              <span class="text-capitalize">{{ item.role }}</span>
            </div>
          </template>

          <!-- 👉 MFA Status -->
          <template #item.mfa_status="{ item }">
            <VChip
              :color="item.has_two_factor_enabled ? 'success' : 'secondary'"
              :variant="item.has_two_factor_enabled ? 'tonal' : 'outlined'"
              size="small"
              label
            >
              <VIcon
                :icon="item.has_two_factor_enabled ? 'tabler-shield-check' : 'tabler-shield-off'"
                size="16"
                class="me-1"
              />
              {{ item.has_two_factor_enabled ? 'Enabled' : 'Disabled' }}
              <span
                v-if="item.has_two_factor_enabled && item.two_factor_primary_method"
                class="text-xs ms-1"
              >
                ({{ item.two_factor_primary_method }})
              </span>
            </VChip>
          </template>

          <!-- Status -->
          <template #item.status="{ item }">
            <VChip
              :color="resolveUserStatusVariant(item.status)"
              size="small"
              label
              class="text-capitalize"
            >
              {{ item.status }}
            </VChip>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <IconBtn
              v-if="$can('delete', 'master.user')"
              :disabled="auth?.user?.id === item.id || (item.is_super_admin && !auth?.user?.is_super_admin)"
              @click="showDeleteConfirmDialog(item)"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                Delete User
              </VTooltip>
              <VIcon icon="tabler-trash" />
            </IconBtn>
            <IconBtn
              v-if="$can('update', 'master.user')"
              :disabled="item.is_super_admin && !auth?.user?.is_super_admin"
            >
              <RouterLink
                :to="{ name: 'app-global-user-tab', params: { id: item.id,tab:'account' } }"
                class="font-weight-medium text-link"
              >
                <VTooltip
                  location="top"
                  activator="parent"
                >
                  Edit User
                </VTooltip>
                <VIcon icon="tabler-edit" />
              </RouterLink>
            </IconBtn>
          </template>
        </VDataTableServer>
      <!-- SECTION -->
      </VCard>  
    </VCol>
    <ShowUserDrawer
      v-model:isDrawerOpen="isShowUserDrawerVisible"
      :user-data="showUserDrawerData"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="User Deleted."
      confirm-title="Deleted!"
      :data="userDeleteData"
      @confirm="userDelete"
    />

    <!-- Import User Modal -->
    <VDialog
      v-model="isImportUserModal"
      max-width="600"
      :persistent="isImportUserLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportUserLoading"
        @click="isImportUserModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Users
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportUserForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportUserLoading"
              :error-messages="importUserErrors?.file"
              @change="
                (e) => {
                  importUserFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText v-if="importUserErrors?.message && !Array.isArray(importUserErrors)">
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importUserErrors.message = null"
          >
            {{ importUserErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->
        <VCardText v-if="Array.isArray(importUserErrors) && importUserErrors.length > 0">
          <div
            class="d-flex flex-column gap-2"
            style="max-block-size: 200px; overflow-y: auto"
          >
            <VAlert
              v-for="(error, index) in importUserErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
            >
              {{ error.row ? `Row ${error.row}: ` : ""
              }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportUserLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportUserLoading"
            @click="downloadSampleUserFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="isImportUserLoading"
            @click="isImportUserModal = false"
          >
            Close
          </VBtn>
          <VBtn
            :loading="isImportUserLoading"
            :disabled="
              isImportUserLoading || !importUserFile
            "
            @click="submitImportUser"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
